import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Printer, Save } from 'lucide-react';

type Ingredient = { id: string; name: string; unitSize: string; unitCost: string; amount: string };
type Props = { onSave: (data: object, name: string) => void; initialData?: object };

const f = (n: number) => `£${isNaN(n) ? '0.00' : n.toFixed(2)}`;

export default function CocktailCalc({ onSave }: Props) {
  const [cocktailName, setCocktailName] = useState('');
  const [ingredients, setIngredients] = useState<Ingredient[]>([
    { id: '1', name: '', unitSize: '700', unitCost: '', amount: '' },
  ]);
  const [garnishCost, setGarnishCost] = useState('');
  const [mixerCost, setMixerCost] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [notes, setNotes] = useState('');

  const addIngredient = () =>
    setIngredients(p => [...p, { id: Date.now().toString(), name: '', unitSize: '700', unitCost: '', amount: '' }]);

  const removeIngredient = (id: string) =>
    setIngredients(p => p.filter(i => i.id !== id));

  const upd = (id: string, f: keyof Ingredient, v: string) =>
    setIngredients(p => p.map(i => i.id === id ? { ...i, [f]: v } : i));

  const results = useMemo(() => {
    const ingCosts = ingredients.map(ing => {
      const us = parseFloat(ing.unitSize) || 0;
      const uc = parseFloat(ing.unitCost) || 0;
      const am = parseFloat(ing.amount) || 0;
      return us > 0 && uc > 0 ? (uc / us) * am : 0;
    });
    const totalCost = ingCosts.reduce((a, b) => a + b, 0)
      + (parseFloat(garnishCost) || 0) + (parseFloat(mixerCost) || 0);
    const sellInc = parseFloat(sellingPrice) || 0;
    const sellEx = sellInc / 1.2;
    const gpP = sellEx > 0 ? ((sellEx - totalCost) / sellEx) * 100 : 0;
    return {
      ingCosts, totalCost, sellInc, sellEx,
      vat: sellInc - sellEx, gpPounds: sellEx - totalCost, gpPct: gpP,
      suggested: [60, 65, 70].map(gp => ({
        gp, exVat: totalCost > 0 ? totalCost / (1 - gp / 100) : 0,
        incVat: totalCost > 0 ? (totalCost / (1 - gp / 100)) * 1.2 : 0,
      })),
    };
  }, [ingredients, garnishCost, mixerCost, sellingPrice]);

  return (
    <div className="space-y-5">
      <div>
        <Label>Cocktail / Drink Name</Label>
        <Input value={cocktailName} onChange={e => setCocktailName(e.target.value)} placeholder="e.g. Aperol Spritz" className="mt-1" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Ingredients</Label>
          <Button size="sm" variant="outline" onClick={addIngredient}><Plus className="w-3.5 h-3.5 mr-1" /> Add Ingredient</Button>
        </div>
        <div className="space-y-1.5">
          <div className="grid grid-cols-12 gap-1.5 text-xs text-muted-foreground font-medium px-1 hidden sm:grid">
            <div className="col-span-3">Ingredient</div><div className="col-span-3">Unit size (ml)</div>
            <div className="col-span-3">Cost per unit (£ ex VAT)</div><div className="col-span-2">Amount (ml)</div>
          </div>
          {ingredients.map((ing, idx) => (
            <div key={ing.id} className="space-y-1.5">
              <div className="grid grid-cols-12 gap-1.5 items-center">
                <Input className="col-span-3" value={ing.name} onChange={e => upd(ing.id, 'name', e.target.value)} placeholder="e.g. Aperol" />
                <Input className="col-span-3" type="number" value={ing.unitSize} onChange={e => upd(ing.id, 'unitSize', e.target.value)} placeholder="700" />
                <Input className="col-span-3" type="number" value={ing.unitCost} onChange={e => upd(ing.id, 'unitCost', e.target.value)} placeholder="0.00" />
                <Input className="col-span-2" type="number" value={ing.amount} onChange={e => upd(ing.id, 'amount', e.target.value)} placeholder="50" />
                <Button className="col-span-1" size="sm" variant="ghost" onClick={() => removeIngredient(ing.id)} disabled={ingredients.length === 1}>
                  <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                </Button>
              </div>
              {results.ingCosts[idx] > 0 && (
                <p className="text-xs text-muted-foreground pl-1">→ Cost per serve: {f(results.ingCosts[idx])}</p>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div><Label>Garnish cost (£)</Label><Input type="number" value={garnishCost} onChange={e => setGarnishCost(e.target.value)} placeholder="0.00" className="mt-1" /></div>
        <div><Label>Mixer / extras (£)</Label><Input type="number" value={mixerCost} onChange={e => setMixerCost(e.target.value)} placeholder="0.00" className="mt-1" /></div>
        <div className="col-span-2 sm:col-span-1"><Label>Selling price inc VAT (£)</Label><Input type="number" value={sellingPrice} onChange={e => setSellingPrice(e.target.value)} placeholder="0.00" className="mt-1" /></div>
      </div>

      {results.totalCost > 0 && (
        <div className="bg-muted/40 border border-border rounded-xl p-4 space-y-3">
          <h3 className="font-semibold text-sm">Calculation Results</h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <span className="text-muted-foreground">Total serve cost:</span><span className="font-medium">{f(results.totalCost)}</span>
            {results.sellInc > 0 && <>
              <span className="text-muted-foreground">Selling price inc VAT:</span><span>{f(results.sellInc)}</span>
              <span className="text-muted-foreground">Selling price ex VAT:</span><span>{f(results.sellEx)}</span>
              <span className="text-muted-foreground">VAT element:</span><span>{f(results.vat)}</span>
              <span className="text-muted-foreground font-medium">Gross profit £:</span>
              <span className={`font-bold ${results.gpPounds >= 0 ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}>{f(results.gpPounds)}</span>
              <span className="text-muted-foreground font-medium">Gross profit %:</span>
              <span className={`font-bold text-2xl ${results.gpPct >= 65 ? 'text-green-600 dark:text-green-400' : results.gpPct >= 55 ? 'text-yellow-600 dark:text-yellow-400' : 'text-destructive'}`}>{results.gpPct.toFixed(1)}%</span>
            </>}
          </div>
          {results.totalCost > 0 && (
            <div className="border-t border-border pt-3">
              <p className="text-xs text-muted-foreground mb-2 font-medium">Suggested selling prices (inc VAT) at target GP:</p>
              <div className="grid grid-cols-3 gap-2">
                {results.suggested.map(s => (
                  <div key={s.gp} className="text-center bg-background rounded-lg p-2.5 border border-border">
                    <div className="text-xs text-muted-foreground">@{s.gp}% GP</div>
                    <div className="font-bold text-sm">{f(s.incVat)}</div>
                    <div className="text-xs text-muted-foreground">ex: {f(s.exVat)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div><Label>Recipe notes / garnish / glass</Label><Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Describe preparation, garnish, glass type, serving instructions..." className="mt-1" /></div>
      <div className="flex gap-2">
        <Button onClick={() => onSave({ cocktailName, ingredients, garnishCost, mixerCost, sellingPrice, notes, results }, cocktailName || 'Cocktail Spec')} className="flex-1">
          <Save className="w-4 h-4 mr-2" /> Save Spec
        </Button>
        <Button variant="outline" onClick={() => window.print()}><Printer className="w-4 h-4 mr-2" /> Print</Button>
      </div>
    </div>
  );
}
